/* =============================================================================
   VIEWS / ADMIN / RESERVATIONS-ACTIONS — Status-Wechsel + Detail-Modal + Reschedule
   =============================================================================
   S69 REF-3 Cut 4/5: aus reservations.js (1.429 LOC) extrahiert.

   Enthält:
     - declineReservation(id)              — Absagen (Status → declined)
     - markAsNoShow(id)                    — No-Show markieren
     - revertNoShow(id)                    — No-Show zurücksetzen
     - openReservationDetail(id)           — Detail-Modal (Switchboard, ~130 LOC)
     - openRescheduleForm(id)              — Verschieben-Form
     - performReschedule(id)               — Verschieben submitten
     - updateReservationDuration(id, raw)  — Dauer-Slider mit Konflikt-Detect
     - deleteReservation(id)               — Soft-Delete + Confirm-Dialog
     - renderDeletedReservationsList()     — Restore-View am Tab-Ende
     - restoreReservation(id)              — Wiederherstellung aus Restore-View

   Cross-Module-Calls (via window):
     - window.confirmReservation, window.changeAssignedTables (confirm.js)
     - window.manuallyCreateOrderForReservation (confirm.js) — im Detail-Modal-Btn
     - window.sendConfirmationEmail / sendDeclineEmail (extern, bootstrap.js)
     - window.openAssignmentPicker (extern, reservation-engine)
============================================================================ */

import {
  htmlEscape, fmtDate, fmtDateShort, todayISO,
  timeToMinutes, minutesToTime, fmtEUR,
  normalizePreorderItems, preorderTotal, preorderSpecialRequests,
  preorderHasItems, showToast
} from '../../utils/helpers.js';
import { closeModal, rerenderActiveTab } from '../../utils/dom.js';
import {
  getReservationDuration,
  reservationOverlaps
} from '../../core/capacity.js';
import { isOrderPaid, isOrderSettled } from '../../core/order-statuses.js';
import { generateTimeSlots } from '../../core/reservation-engine.js';
import {
  getState, tableNamesFor, areaLabel, statusBadge
} from './reservations-helpers.js';
import {
  getCustomerHistory, splitCustomerHistory, customerStatusBadge
} from './reservations-customers.js';
// S239-HF3 — Check-in-Button-State aus derselben Quelle wie der Belegungsplan.
import { findCheckinOccupancy, reservationCheckinButton, reservationNoShowButton } from './reservation-checkin.js';

// -----------------------------------------------------------------------------
// S147 — Bestell-Button-State im Reservierungs-Detail (mehrere Runden wie Walk-In)
// -----------------------------------------------------------------------------
/**
 * Pure Button-State-Logik für „Bestellung aufnehmen" im Reservierungs-Detail.
 *
 * Reservierungen können seit S135-P4 mehrere Runden auf EINE offene Order
 * anhängen (Append-Weiche in admin-order-entry-submit.js — eine Rechnung,
 * separater Küchenlauf), exakt wie Walk-Ins. Der Button bleibt darum sichtbar
 * und schaltet nach der ersten offenen Order von „Bestellung aufnehmen" auf
 * „Weitere Bestellung aufnehmen" um. Erst eine bezahlte/abgeschlossene Order
 * schließt den Bestell-Einstieg (Reservierung schließt über die Zahlung).
 *
 * Der Vorbestellung-noch-nicht-in-der-Küche-Fall hat einen EIGENEN Pfad
 * („Jetzt an Küche senden" im Vorbestellungs-Block) → hier dann kein Button.
 *
 * @param {object} reservation — die Reservierung (status etc.)
 * @param {object|null} linkedOrder — verknüpfte, nicht stornierte Order (oder null)
 * @param {{hasPreorder?:boolean}} [opts] — Vorbestellung mit Items vorhanden?
 * @returns {{show:boolean,label:string,action:string}}
 *
 * Pure — kein DOM, Vitest-fähig.
 */
export function reservationOrderButton(reservation, linkedOrder, { hasPreorder = false } = {}) {
  const none = { show: false, label: '', action: '' };
  if (!reservation || reservation.status !== 'confirmed') return none;
  // S147b — Bezahlt schließt den Einstieg. Eine Dine-In-Reservierungs-Order
  // bleibt nach Zahlung `status=confirmed`/`payment_status=paid` und erreicht
  // NIE `picked_up` → `isOrderSettled` (braucht picked_up) wird hier nie wahr.
  // Darum zusätzlich auf `isOrderPaid` gaten. `isOrderSettled` deckt no_show ab.
  if (linkedOrder && (isOrderPaid(linkedOrder) || isOrderSettled(linkedOrder))) return none;
  // Offene/unbezahlte Order verknüpft → weitere Runde anhängen (eine Rechnung).
  if (linkedOrder) {
    return { show: true, label: 'Weitere Bestellung aufnehmen', action: 'takeReservationOrder' };
  }
  // Keine Order, aber Vorbestellung noch nicht in der Küche → eigener Pfad.
  if (hasPreorder) return none;
  // Keine Order, keine Vorbestellung → erste Bestellung aufnehmen.
  return { show: true, label: 'Bestellung aufnehmen', action: 'takeReservationOrder' };
}

// -----------------------------------------------------------------------------
// Status-Aktionen
// -----------------------------------------------------------------------------
export async function declineReservation(id) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  if (!r) return;
  if (!(await window.styledConfirm({ title: `Reservierung von ${r.name} absagen?`, body: `${r.date} ${r.time}`, danger: true, confirmLabel: 'Absagen' })).confirmed) return;
  const updated = await window.App.storage.reservations.update(id, {
    status: 'declined',
    declinedAt: new Date().toISOString()
  });
  // Auch hier KEIN Direkt-Aufruf von sendDeclineEmail mehr — analog zum
  // confirm-Pfad, der Event-Handler in bootstrap.js übernimmt den Mail-
  // Versand (Funktionstest Audit-S21, Doppel-Mail-Bug).
  await window.App.events.emit('reservation:declined', updated);
  rerenderActiveTab();
}

export async function markAsNoShow(id) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  if (!r) return;
  if (!(await window.styledConfirm({ title: `${r.name} als nicht erschienen markieren?`, confirmLabel: 'Markieren' })).confirmed) return;
  const updated = await window.App.storage.reservations.update(id, {
    status: 'no-show',
    noShowAt: new Date().toISOString()
  });
  await window.App.events.emit('reservation:noshow', updated);
  showToast('Als No-Show markiert', 'success');
  // S240-HF6: Das Detail-Modal blieb offen und zeigte den ALTEN Stand
  // („bestätigt · ✅ da seit … · 🪑 Belegung öffnen · No-Show") — ein zweiter
  // Klick auf No-Show wäre möglich gewesen. Schließen wie beim Check-in; die
  // Liste dahinter zieht über rerenderActiveTab nach.
  closeModal();
  rerenderActiveTab();
}

export async function revertNoShow(id) {
  const updated = await window.App.storage.reservations.update(id, {
    status: 'confirmed',
    noShowAt: null
  });
  await window.App.events.emit('reservation:changed', { reservation: updated, fields: ['status'] });
  rerenderActiveTab();
}

// -----------------------------------------------------------------------------
// Detail-Modal
// -----------------------------------------------------------------------------
export function openReservationDetail(id) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  if (!r) return;
  const total = preorderTotal(r.preorder);
  const preorderItems = normalizePreorderItems(r.preorder);
  const specialReq = preorderSpecialRequests(r.preorder);
  const history = getCustomerHistory(r);
  // S146 — Historie nach Datum trennen: echte Vergangenheit vs. kommende/parallele
  // Buchungen (heute + Zukunft). Vorher landeten heutige + zukünftige Buchungen
  // fälschlich unter „Vergangene Besuche". Das VIP/Stammgast-Badge stützt sich
  // jetzt auf die ECHTEN Vorbesuche (past), nicht auf parallel angelegte Buchungen.
  const { past: pastVisits, upcoming: otherBookings } = splitCustomerHistory(history, todayISO());
  // S112 — Verknüpfte Order als Single-Source für Modal-Spiegelung + Doppel-Order-
  // Guard. Operator-aufgenommene Orders (S111-Button + post-confirm „Ja") verknüpfen
  // NUR über `orders.reservation_id` und füllen `r.preorder` NICHT — die Anzeige/
  // der Guard dürfen also nicht an `r.preorder` hängen, sondern an dieser Order.
  const linkedOrder = (state.orders || []).find(
    o => o.reservationId === r.id && o.status !== 'cancelled'
  );
  // S147 / S147b — Nur eine OFFENE Order zählt als „in Bearbeitung". Offen =
  // verknüpft UND nicht bezahlt UND nicht settled. Bezahlt (payment_status paid)
  // schließt den Einstieg, auch wenn die Order mangels `picked_up` nie
  // `isOrderSettled` erreicht (Dine-In-Reservierungs-Order, S147b-Befund).
  const openOrder = (linkedOrder && !isOrderPaid(linkedOrder) && !isOrderSettled(linkedOrder))
    ? linkedOrder : null;
  // S147 — Bestell-Button-State (mehrere Runden wie Walk-In). Standardmäßig
  // sichtbar; Label/Append-Verhalten ergeben sich aus dem verknüpften Order-State.
  const orderBtn = reservationOrderButton(r, linkedOrder, {
    hasPreorder: preorderHasItems(r.preorder)
  });
  // S239-HF3 — „✅ Gäste sind da" + „No-Show" gehören dorthin, wo der Service die
  // Reservierung anfasst. Bis hierher waren beide im Detail-Modal nicht erreichbar:
  // der Check-in nur im Belegungsplan, `markAsNoShow` nur in `renderReservationItem`
  // (diese Listen-Sicht ist seit S108 nicht mehr gemountet — im Cut-A0-Test musste
  // No-Show über die Konsole ausgelöst werden). Der Button-State kommt aus DEMSELBEN
  // Helfer wie in occupancy.js (Single-Source, kein zweites Markup).
  const checkinOcc = findCheckinOccupancy(r.id, state.tableOccupancies);
  const checkinBtn = reservationCheckinButton(r, checkinOcc);
  const arrivedBadge = (checkinOcc && r.arrivedAt)
    ? `<span class="badge badge-completed" title="Check-in">✅ da seit ${htmlEscape(new Date(r.arrivedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }))} Uhr</span>`
    : '';
  // S240-HF6: No-Show nur für heute/vergangen (Jonas-Entscheid) — gleiche
  // Helfer-Ebene wie der Check-in, damit beide Buttons EINE Wahrheit haben.
  const noShowBtn = reservationNoShowButton(r, {
    featureEnabled: !!(window.AppConfig && window.AppConfig.features
      && window.AppConfig.features.noShowTracking)
  });
  const html = `
    <div class="modal-header">
      <h2>Reservierung · ${htmlEscape(r.name)}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field" style="margin-bottom: 12px;">${customerStatusBadge(pastVisits)}</div>
    <div class="field-row">
      <div><strong>Datum:</strong> ${fmtDate(r.date)}</div>
      <div><strong>Uhrzeit:</strong> ${r.time} Uhr</div>
    </div>
    <div class="field-row">
      <div><strong>Personen:</strong> ${r.guests}</div>
      <div><strong>Status:</strong> ${statusBadge(r)}${r.serviceType === 'takeaway' ? ' <span class="badge badge-takeaway">🥡 Abholung</span>' : ''}${arrivedBadge ? ' ' + arrivedBadge : ''}</div>
    </div>
    <div class="field-row" style="align-items:center;">
      <div style="display:flex; align-items:center; gap:8px;">
        <strong>Dauer:</strong>
        <input id="rd-duration-${r.id}" type="number" min="30" max="240" step="30"
               value="${getReservationDuration(r)}"
               onchange="updateReservationDuration('${r.id}', this.value)"
               style="width:80px; padding:4px 6px;">
        <span style="font-size:.85rem; color:var(--ink-soft);">Min.</span>
        <span id="rd-duration-end-${r.id}" style="font-size:.85rem; color:var(--ink-soft);">bis ${minutesToTime((timeToMinutes(r.time) + getReservationDuration(r)) % (24*60))} Uhr</span>
      </div>
      <div></div>
    </div>
    <div class="field-row">
      <div><strong>Telefon:</strong> <a href="tel:${htmlEscape(r.phone)}">${htmlEscape(r.phone)}</a></div>
      <div><strong>E-Mail:</strong> <a href="mailto:${htmlEscape(r.email)}">${htmlEscape(r.email)}</a></div>
    </div>
    <div class="field">
      <strong>Bereich / Tische:</strong>
      ${r.assignedTables && r.assignedTables.length
        ? (() => { const uc = Number(r.extraChairsUsed) || (r.needsExtraChair ? 1 : 0); return `${areaLabel((state.tables.find(t=>t.id===r.assignedTables[0])||{}).area || r.area)} · Tische: ${htmlEscape(tableNamesFor(r.assignedTables))}${r.tablesCombined ? ' (kombiniert)' : ''}${uc > 0 ? ` + ${uc} Zusatzstuhl${uc > 1 ? 'e' : ''}` : ''}`; })()
        : areaLabel(r.area) + ' (noch nicht zugewiesen)'}
    </div>
    ${r.notes ? `<div class="field"><strong>Bemerkung:</strong><div class="notes">${htmlEscape(r.notes)}</div></div>` : ''}
    ${preorderItems.length ? `
      <div class="field">
        <strong>Vorbestellung:</strong>
        <div class="notes">
          ${preorderItems.map(it => `
            ${it.qty}× ${htmlEscape(it.displayName)} — ${fmtEUR(it.lineTotal)}
            ${it.note ? `<div style="font-size:.85rem; color:var(--ink-soft); margin-left:14px;">${htmlEscape(it.note)}</div>` : ''}
          `).join('<br>')}
          <div style="margin-top: 6px; font-weight: 600;">Summe: ${fmtEUR(total)}</div>
          ${specialReq ? `<div style="margin-top: 8px; padding: 6px 8px; background: var(--warn-soft); border-radius: 4px;"><strong>Sonderwunsch:</strong> ${htmlEscape(specialReq)}</div>` : ''}
          ${(r.status === 'confirmed' && !linkedOrder) ? `
            <div style="margin-top: 10px; padding: 8px 10px; background: var(--warn-soft); border-left: 3px solid var(--warn); border-radius: 4px; font-size: .9rem; display: flex; justify-content: space-between; align-items: center; gap: 10px;">
              <span>⚠️ Vorbestellung ist noch nicht in der Küche/Theke.</span>
              <button class="btn btn-sm btn-secondary" onclick="manuallyCreateOrderForReservation('${r.id}')">Jetzt an Küche senden</button>
            </div>
          ` : ''}
        </div>
      </div>
    ` : ''}
    ${openOrder ? `
      <div class="field">
        <div style="padding: 8px 10px; background: var(--green-soft); border-left: 3px solid var(--green); border-radius: 4px; font-size: .9rem;">
          📋 <strong>In Bearbeitung:</strong> Order <code>${htmlEscape(openOrder.orderNumber || openOrder.id)}</code>
          · Status <em>${htmlEscape(openOrder.status)}</em>
        </div>
      </div>
    ` : ''}
    ${r.declineHint ? `<div class="field"><strong>Hinweis Regelprüfung:</strong><div class="notes" style="background:var(--danger-soft); color:var(--danger);">${htmlEscape(r.declineHint)}</div></div>` : ''}
    ${pastVisits.length ? `
      <div class="field">
        <strong>Vergangene Besuche (${pastVisits.length}):</strong>
        <div class="customer-history">
          ${pastVisits.slice(0, 8).map(h => `
            <div class="history-row">
              <span class="history-date">${fmtDateShort(h.date)} · ${h.time}</span>
              <span class="history-guests">${h.guests}P</span>
              <span class="history-status">${statusBadge(h)}</span>
            </div>
          `).join('')}
          ${pastVisits.length > 8 ? `<div class="history-more">+ ${pastVisits.length - 8} weitere</div>` : ''}
        </div>
      </div>
    ` : ''}
    ${otherBookings.length ? `
      <div class="field">
        <strong>Weitere Buchungen dieses Gastes (${otherBookings.length}):</strong>
        <div class="customer-history">
          ${otherBookings.slice(0, 8).map(h => `
            <div class="history-row">
              <span class="history-date">${fmtDateShort(h.date)} · ${h.time}</span>
              <span class="history-guests">${h.guests}P</span>
              <span class="history-status">${statusBadge(h)}</span>
            </div>
          `).join('')}
          ${otherBookings.length > 8 ? `<div class="history-more">+ ${otherBookings.length - 8} weitere</div>` : ''}
        </div>
      </div>
    ` : ''}
    <div class="field" style="font-size:.8rem; color:var(--ink-muted);">
      Eingegangen: ${r.createdAt ? new Date(r.createdAt).toLocaleString('de-DE') : '—'}
      ${r.confirmedAt ? '· Bestätigt: ' + new Date(r.confirmedAt).toLocaleString('de-DE') : ''}
      ${r.declinedAt ? '· Abgesagt: ' + new Date(r.declinedAt).toLocaleString('de-DE') : ''}
    </div>
    <div class="btn-row" style="margin-top: 16px;">
      ${r.status === 'pending' ? `
        <button class="btn btn-primary" onclick="closeModal(); confirmReservation('${r.id}');">Tische wählen &amp; bestätigen</button>
        <button class="btn btn-danger" onclick="declineReservation('${r.id}'); closeModal();">Absagen &amp; mailen</button>
      ` : ''}
      ${r.status === 'confirmed' ? `
        ${checkinBtn.show ? (checkinBtn.mode === 'checkin'
          ? `<button class="btn btn-primary" onclick="checkinReservation('${htmlEscape(r.id)}')" title="Gäste sind eingetroffen — Tisch wird belegt, ab hier wie ein Walk-In">${checkinBtn.label}</button>`
          : `<button class="btn btn-secondary" onclick="closeModal(); openOccupancyWalkInModal('${htmlEscape(checkinBtn.occupancyId)}')">${checkinBtn.label}</button>`) : ''}
        <button class="btn btn-secondary" onclick="closeModal(); changeAssignedTables('${r.id}');">Tische ändern</button>
        ${orderBtn.show ? `
          <button class="btn btn-secondary" onclick="closeModal(); ${orderBtn.action}('${r.id}')">${orderBtn.label}</button>
        ` : ''}
        ${noShowBtn.show && noShowBtn.mode === 'noshow' ? `
          <button class="btn btn-ghost" onclick="markAsNoShow('${htmlEscape(r.id)}')" title="Gäste sind nicht erschienen — gibt den Tisch frei">${noShowBtn.label}</button>
        ` : ''}
      ` : ''}
      ${noShowBtn.show && noShowBtn.mode === 'revert' ? `
        <button class="btn btn-ghost" onclick="revertNoShow('${htmlEscape(r.id)}')">${noShowBtn.label}</button>
      ` : ''}
      ${r.status === 'declined' ? `
        <button class="btn btn-secondary" onclick="sendDeclineEmail('${r.id}')">Absagemail</button>
      ` : ''}
      <button class="btn btn-secondary" onclick="openRescheduleForm('${r.id}')">Verschieben</button>
      <button class="btn btn-ghost" onclick="deleteReservation('${r.id}')">Löschen</button>
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

// -----------------------------------------------------------------------------
// Reschedule + Dauer + Delete
// -----------------------------------------------------------------------------
export function openRescheduleForm(id) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  if (!r) return;
  const slots = generateTimeSlots();
  const html = `
    <div class="modal-header">
      <h2>Reservierung verschieben</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="color: var(--ink-soft); font-size: .9rem;">
      Bisher: <strong>${fmtDate(r.date)} um ${r.time} Uhr</strong> für ${r.guests} ${r.guests===1?'Person':'Personen'}.
      Nach dem Verschieben prüfen wir, ob die Tische noch passen — ggf. weist du sie neu zu.
    </p>
    <div class="field-row">
      <div class="field">
        <label>Neues Datum</label>
        <input id="rs-date" type="date" value="${r.date}" min="${todayISO()}">
      </div>
      <div class="field">
        <label>Neue Uhrzeit</label>
        <select id="rs-time">
          ${slots.map(s => `<option value="${s}" ${s===r.time?'selected':''}>${s}</option>`).join('')}
        </select>
      </div>
    </div>
    <div class="field">
      <label>Anzahl Personen</label>
      <input id="rs-guests" type="number" min="1" max="20" value="${r.guests}">
    </div>
    <div class="field">
      <label class="checkbox">
        <input type="checkbox" id="rs-clear-tables" checked>
        Bisherige Tisch-Zuweisung zurücksetzen (Neu-Auswahl beim Verschieben)
      </label>
    </div>
    <div class="btn-row">
      <button class="btn btn-primary" onclick="performReschedule('${r.id}')">Verschieben</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export async function performReschedule(id) {
  const date = document.getElementById('rs-date').value;
  const time = document.getElementById('rs-time').value;
  const guests = parseInt(document.getElementById('rs-guests').value, 10);
  const clearTables = document.getElementById('rs-clear-tables').checked;
  if (!date || !time || !guests) {
    showToast('Bitte alle Felder ausfüllen', 'error');
    return;
  }
  if (date < todayISO()) {
    showToast('Datum liegt in der Vergangenheit', 'error');
    return;
  }
  const changes = { date, time, guests };
  if (clearTables) {
    changes.assignedTables = [];
    changes.tablesCombined = false;
    changes.needsExtraChair = false;
    changes.extraChairsUsed = 0;
  }
  const updated = await window.App.storage.reservations.update(id, changes);
  await window.App.events.emit('reservation:changed', { reservation: updated, fields: Object.keys(changes) });
  closeModal();
  showToast('Reservierung verschoben', 'success');
  rerenderActiveTab();
  if (clearTables && updated.status === 'confirmed') {
    setTimeout(() => {
      if (typeof window.changeAssignedTables === 'function') {
        window.changeAssignedTables(id);
      }
    }, 300);
  }
}

export async function updateReservationDuration(id, rawValue) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  if (!r) return;
  let v = parseInt(rawValue, 10);
  if (!Number.isFinite(v) || v < 30) v = 30;
  if (v > 240) v = 240;
  v = Math.round(v / 30) * 30;
  const prev = getReservationDuration(r);
  if (v === prev) return;

  const probe = { date: r.date, time: r.time, durationMin: v };
  const myTables = new Set(r.assignedTables || []);
  const conflicts = [];
  for (const other of (state.reservations || [])) {
    if (other.id === r.id) continue;
    if (other.status === 'declined' || other.status === 'cancelled') continue;
    if (!(other.assignedTables || []).some(t => myTables.has(t))) continue;
    if (reservationOverlaps(probe, other)) conflicts.push(other);
  }

  const updated = await window.App.storage.reservations.update(id, { durationMin: v });
  await window.App.storage.audit.add({
    type: 'reservation:duration-changed',
    reservationId: r.id,
    from: prev,
    to: v,
    actor: (window.App.auth && window.App.auth.currentUser && window.App.auth.currentUser().name) || 'admin'
  });
  await window.App.events.emit('reservation:changed', { reservation: updated, fields: ['durationMin'] });

  const endSpan = document.getElementById(`rd-duration-end-${id}`);
  if (endSpan) {
    endSpan.textContent = `bis ${minutesToTime((timeToMinutes(r.time) + v) % (24*60))} Uhr`;
  }
  if (conflicts.length) {
    const names = conflicts.map(c => `${c.name} (${c.time})`).join(', ');
    showToast(`Dauer gespeichert (${v} Min.) — Achtung: ${conflicts.length} Konflikt${conflicts.length > 1 ? 'e' : ''} mit ${names}`, 'warn');
  } else {
    showToast(`Dauer auf ${v} Min. aktualisiert`, 'success');
  }
  rerenderActiveTab();
}

export async function deleteReservation(id) {
  // S59 Block A (Phase 46): Soft-Delete statt Hard-Delete. Analog zu menu_items
  // + tables (Phase 45). Unterschied zu Storno: deleted_at blendet die
  // Reservierung aus den aktiven Listen aus, cancelled_at lässt sie sichtbar
  // (Storno bleibt im Report). Wiederherstellbar via „Gelöschte
  // Reservierungen"-Sektion unten.
  const _del = await window.styledConfirm({
    title: 'Reservierung aus der Liste entfernen?', danger: true, confirmLabel: 'Entfernen',
    body: 'Kein Hard-Delete — der Eintrag bleibt in der DB und ist im Bereich „🗑️ Gelöschte Reservierungen" wiederherstellbar.\n\nHinweis: Das ist NICHT „Stornieren" — Storno bleibt in Reports sichtbar, Soft-Delete blendet nur aus.',
    reasonField: true
  });
  if (!_del.confirmed) return;
  const reason = _del.reason || '';
  try {
    await window.App.storage.reservations.softDelete(id, reason);
    closeModal();
    rerenderActiveTab();
    await renderDeletedReservationsList();
    showToast('Reservierung gelöscht (wiederherstellbar)', 'success');
  } catch (e) {
    console.error('[deleteReservation]', e);
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * S59 Block A — Restore-View für soft-gelöschte Reservierungen.
 * Analog zu renderDeletedMenuList (menu-editor.js) und renderDeletedTablesList
 * (layout-editor.js). Lädt deleted_at IS NOT NULL aus der DB (nicht im
 * aktiven _state.reservations) und rendert als Karten-Liste.
 */
export async function renderDeletedReservationsList() {
  const wrap = document.getElementById('reservations-deleted-list');
  if (!wrap) return;
  let items = [];
  try {
    items = await window.App.storage.reservations.listDeleted();
  } catch (e) {
    console.warn('[reservations] listDeleted:', e);
  }
  if (!items.length) {
    wrap.innerHTML = `
      <div class="hint" style="padding:12px; text-align:center;">
        Keine gelöschten Reservierungen. (Hier landen Einträge nach „Löschen".)
      </div>`;
    return;
  }
  const AREA_LABELS = { indoor: 'Innen', outdoor: 'Außen', club: 'Club' };
  wrap.innerHTML = `
    <div style="font-size:.85rem; color:var(--ink-soft); margin-bottom:10px;">
      ${items.length} Eintrag/Einträge — Wiederherstellung möglich, solange die
      Reservierung nicht final aus der DB entfernt wurde.
    </div>
    <div style="display:flex; flex-direction:column; gap:8px;">
    ${items.map(r => `
      <div data-deleted-reservation="${htmlEscape(r.id)}"
           style="display:grid; grid-template-columns:1fr auto; gap:12px; align-items:center;
                  padding:10px 14px; background:var(--bg-soft);
                  border:1px solid var(--border); border-radius:8px;">
        <div style="min-width:0;">
          <div style="font-weight:600; line-height:1.2;">${htmlEscape(r.name || '—')}</div>
          <div style="color:var(--ink-soft); font-size:.82rem; margin-top:2px;">
            ${r.date ? fmtDate(r.date) : '—'} ${r.time || ''} · ${r.guests || 0} ${r.guests === 1 ? 'Person' : 'Personen'}
            · ${AREA_LABELS[r.area] || r.area || '—'}
            · gelöscht ${r.deletedAt ? new Date(r.deletedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
          </div>
          ${r.deletedReason ? `
            <div style="color:var(--ink-soft); font-size:.78rem; margin-top:2px; font-style:italic;">
              Grund: ${htmlEscape(r.deletedReason)}
            </div>` : ''}
        </div>
        <button class="btn btn-primary btn-sm" style="white-space:nowrap;"
                onclick="restoreReservation('${r.id}')">
          ↺ Wiederherstellen
        </button>
      </div>
    `).join('')}
    </div>
  `;
}

export async function restoreReservation(id) {
  try {
    await window.App.storage.reservations.restore(id);
    rerenderActiveTab();
    await renderDeletedReservationsList();
    showToast('Reservierung wiederhergestellt', 'success');
  } catch (e) {
    console.error('[restoreReservation]', e);
    showToast('Wiederherstellen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// window-Bindings für inline-onclick-Konsumenten.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    declineReservation, markAsNoShow, revertNoShow,
    openReservationDetail,
    openRescheduleForm, performReschedule,
    updateReservationDuration, deleteReservation,
    renderDeletedReservationsList, restoreReservation
  });
}
